/**
 * MemorialPublicExperience — premium tribute-first published memorial layout.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Download,
  ExternalLink,
  Flame,
  Flower2,
  Gem,
  Heart,
  MessageSquare,
  QrCode,
  Share2,
} from 'lucide-react';
import { BrandWordmark } from '../brand/BrandWordmark';
import { Button } from '../ui/button';
import { PublicFooter } from '../layout/PublicFooter';
import { MemorialReactions } from './MemorialReactions';
import { MemorialPublicNav } from './MemorialPublicNav';
import { MemorySoundtrackSection } from './MemorySoundtrackSection';
import { MemorialPublicVideoCard } from './MemorialPublicVideoCard';
import { LeaveTributeForm, TRIBUTE_REACTION_OPTIONS } from './LeaveTributeForm';
import { MemorialOwnerToolbar } from './MemorialOwnerToolbar';
import {
  MEMORIAL_COPY,
  MEMORIAL_PHOTO_LIMIT,
  MEMORIAL_PUBLIC_TABS,
  mergePublicSections,
  resolveMemorialCoverUrl,
  resolveMemorialPortraitUrl,
} from '../../utils/memorial';
import { POSSESSION_CATEGORY_LABELS } from '../../utils/memoryMarkers';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { hasVideoLink } from '../../utils/videoLink';
import { getMemorialAppearanceProps, ATMOSPHERE_FIELD_LABELS } from '../../utils/memorialAppearance';
import { formatDisplayDate, formatPersonDateRange } from '../../utils/dateDisplay';
import { toast } from 'sonner';
import '../../styles/memorial-public.css';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

function personDisplayName(person) {
  return [person?.given_names, person?.surname].filter(Boolean).join(' ') || 'Remembered';
}

function personInitials(person) {
  const g = (person?.given_names || '')[0] || '';
  const s = (person?.surname || '')[0] || '';
  return (g + s).toUpperCase() || '?';
}

function formatDates(person) {
  return formatPersonDateRange(person);
}

function tributeReactionEmoji(reactionType) {
  return TRIBUTE_REACTION_OPTIONS.find((r) => r.key === reactionType)?.emoji || null;
}

function MemorialPrivacyFooter() {
  return (
    <div className="memorial-public__privacy-compact" data-testid="memorial-privacy-footer">
      <p>
        <strong>A memorial page you can trust.</strong>
        {' '}
        Published only when the family chooses. Tributes are moderated before they appear.
      </p>
      <p className="mt-2">
        No ads. No private archive data on this page. Preserved with MyLegacyBridge.
      </p>
    </div>
  );
}

function MemorialHero({
  person,
  memorial,
  coverUrl,
  portraitUrl,
  onShare,
  onScrollToQr,
  reactions,
}) {
  const name = memorial?.title || personDisplayName(person);
  const dates = formatDates(person);
  const tributeLine = memorial?.intro_message || person?.remembrance_line;
  const reactionTotal = Object.values(reactions || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

  return (
    <header className="memorial-public__hero-band">
      <div className="memorial-public__hero w-full">
        <div className="memorial-public__hero-bar">
          <div className="mx-auto flex max-w-[var(--memorial-content-max,72rem)] items-center justify-between gap-4 px-4 py-3 sm:px-8">
            <BrandWordmark className="memorial-public__wordmark h-6 sm:h-7" />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button type="button" variant="outline" size="sm" className="memorial-public__btn-hero rounded-full" onClick={onShare}>
                <Share2 className="mr-1.5 h-3.5 w-3.5" />
                Share
              </Button>
              <Button type="button" variant="outline" size="sm" className="memorial-public__btn-hero rounded-full" onClick={onScrollToQr}>
                <QrCode className="mr-1.5 h-3.5 w-3.5" />
                QR / Link
              </Button>
            </div>
          </div>
        </div>

        <div
          className={`memorial-public__hero-cover ${coverUrl ? 'memorial-public__hero-cover--image' : 'memorial-public__hero-cover--placeholder'}`}
          style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
          aria-hidden
        />
        <div className="memorial-public__hero-overlay" aria-hidden />

        <div className="memorial-public__hero-body">
          <div className="flex flex-col items-center text-center">
            {portraitUrl ? (
              <img
                src={portraitUrl}
                alt=""
                className="memorial-public__portrait-frame h-32 w-32 rounded-2xl object-cover sm:h-40 sm:w-40"
              />
            ) : (
              <div className="memorial-public__portrait-frame flex h-32 w-32 items-center justify-center rounded-2xl bg-[hsl(42_28%_96%)] font-serif text-3xl text-[hsl(38_36%_42%)]/80 sm:h-40 sm:w-40 sm:text-4xl">
                {personInitials(person)}
              </div>
            )}

            <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-[hsl(32_10%_42%)]">
              {memorial?.subtitle || MEMORIAL_COPY.rememberedHere}
            </p>
            <h1 className="memorial-public__hero-title mt-3 text-3xl sm:text-4xl md:text-5xl">{name}</h1>
            {dates ? <p className="memorial-public__hero-meta mt-2 text-sm tabular-nums">{dates}</p> : null}
            {tributeLine ? (
              <p className="memorial-public__hero-epitaph mx-auto mt-5 max-w-2xl font-serif text-lg italic leading-relaxed sm:text-xl">
                &ldquo;{tributeLine}&rdquo;
              </p>
            ) : null}

            {reactionTotal > 0 ? (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                {(reactions?.candle || 0) > 0 ? (
                  <span className="memorial-public__reaction-pill">
                    <Flame className="h-3.5 w-3.5 text-[hsl(24_60%_45%)]" />
                    {reactions.candle}
                  </span>
                ) : null}
                {(reactions?.flower || 0) > 0 ? (
                  <span className="memorial-public__reaction-pill">
                    <Flower2 className="h-3.5 w-3.5 text-[hsl(152_28%_32%)]" />
                    {reactions.flower}
                  </span>
                ) : null}
                {(reactions?.love || 0) > 0 ? (
                  <span className="memorial-public__reaction-pill">
                    <Heart className="h-3.5 w-3.5 text-[hsl(350_45%_48%)]" />
                    {reactions.love}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}

function OwnerEmptyNote({ children }) {
  return <p className="memorial-public__empty-note">{children}</p>;
}

function MemorialAtmosphereSection({ atmosphere }) {
  if (!atmosphere || !Object.keys(atmosphere).length) return null;
  const entries = Object.entries(atmosphere).filter(([, v]) => v);
  if (!entries.length) return null;

  return (
    <section id="atmosphere" className="memorial-public__section">
      <h2 className="memorial-public__section-title">Remembered through the senses</h2>
      <div className="memorial-public__card p-6 sm:p-8">
        <ul className="grid gap-4 sm:grid-cols-2">
          {entries.map(([key, value]) => (
            <li key={key} className="rounded-xl border border-[hsl(38_14%_86%)]/80 bg-white/60 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(32_10%_42%)]">
                {ATMOSPHERE_FIELD_LABELS[key] || key}
              </p>
              <p className="mt-1.5 font-serif text-base leading-relaxed text-[hsl(24_14%_16%)]">{value}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function MemorialPublicExperience({
  slug = null,
  person,
  memorial = {},
  sections: sectionsProp = {},
  biography = null,
  writtenMemories = [],
  timeline = [],
  photoGallery = [],
  memories = [],
  profileVideo = null,
  videoLinks = [],
  music = null,
  soundtrack = null,
  atmosphere = null,
  treasuredThings = [],
  tributes = [],
  qr = null,
  reactions = {},
  support = null,
  allowTributes = true,
  requireEmailForTributes = false,
  enableReactions = true,
  showReactionCounts = true,
  ownerPreview = false,
  manageUrl = null,
  onShare,
  onTributeSubmitted,
}) {
  const sectionRefs = useRef({});
  const [activeTab, setActiveTab] = useState('life-story');

  const sections = useMemo(() => mergePublicSections(sectionsProp), [sectionsProp]);

  const appearanceProps = useMemo(
    () => getMemorialAppearanceProps(memorial?.appearance, (url) => resolveAssetUrl(BACKEND, url)),
    [memorial?.appearance],
  );

  const coverUrl = useMemo(
    () => resolveMemorialCoverUrl(memorial, person, BACKEND, resolveAssetUrl),
    [memorial, person],
  );

  const portraitUrl = useMemo(
    () => resolveMemorialPortraitUrl(memorial, person, BACKEND, resolveAssetUrl),
    [memorial, person],
  );

  const photos = (photoGallery || []).slice(0, MEMORIAL_PHOTO_LIMIT);
  const videoMemories = (memories || []).filter((m) => hasVideoLink(m));
  const voiceMemories = (memories || []).filter((m) => m.has_voice || m.audio_file_url || (m.audio_segments || []).length);
  const externalVideos = (videoLinks || []).map((v) => {
    if (typeof v === 'string') return { video_link: { video_url: v } };
    if (v.video_link) return v;
    return {
      id: v.id,
      title: v.title,
      description: v.description,
      video_link: v.url ? { video_url: v.url, video_title: v.title } : v,
    };
  });

  const hasLifeStoryContent = Boolean(biography || timeline?.length || writtenMemories?.length);
  const hasGalleryContent = photos.length > 0;
  const hasVoiceContent = voiceMemories.length > 0;
  const hasMusicContent = Boolean(
    music?.featured?.link_url
    || (music?.tracks || []).length
    || soundtrack?.url,
  );
  const hasVideoContent = Boolean(profileVideo || videoMemories.length > 0 || externalVideos.length > 0);
  const hasAtmosphere = Boolean(atmosphere && Object.keys(atmosphere).length);

  const publicMemorialUrl = memorial?.public_url || (slug && typeof window !== 'undefined' ? `${window.location.origin}/memorial/${slug}` : null);
  const qrImageSrc = slug ? `${API}/public/memorial/${slug}/qr.png` : null;
  const dynamicQrUrl = qr?.redirect_url || null;

  const visibleTabs = useMemo(() => {
    const checks = {
      'life-story': sections.show_life_story,
      gallery: sections.show_gallery && (hasGalleryContent || ownerPreview),
      tributes: sections.show_tributes,
      remembrance: sections.show_reactions && enableReactions,
      'voice-music':
        (sections.show_voice || sections.show_soundtrack)
        && (hasVoiceContent || hasMusicContent || ownerPreview),
      videos: sections.show_videos && (hasVideoContent || ownerPreview),
      'qr-keepsakes': sections.show_qr || sections.show_keepsakes,
      support: sections.show_support && support?.enabled && support?.url,
      atmosphere: sections.show_atmosphere && (hasAtmosphere || ownerPreview),
    };
    return MEMORIAL_PUBLIC_TABS.filter((tab) => {
      if (tab.id === 'voice-music') return checks['voice-music'];
      if (tab.id === 'qr-keepsakes') return checks['qr-keepsakes'];
      return checks[tab.id];
    });
  }, [
    sections,
    hasGalleryContent,
    enableReactions,
    hasVoiceContent,
    hasMusicContent,
    hasVideoContent,
    hasAtmosphere,
    support,
    ownerPreview,
  ]);

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.find((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const scrollToSection = useCallback((tabId) => {
    setActiveTab(tabId);
    const el = sectionRefs.current[tabId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleShare = onShare || (async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ url, title: document.title });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  });

  const copyLink = async () => {
    const target = dynamicQrUrl || publicMemorialUrl;
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target);
      toast.success(dynamicQrUrl ? 'QR link copied.' : 'Link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const downloadQr = () => {
    if (!qrImageSrc) return;
    const a = document.createElement('a');
    a.href = qrImageSrc;
    a.download = `memorial-${slug || 'qr'}.png`;
    a.click();
  };

  const setSectionRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div
      className={`memorial-public memorial-public__page-bg flex min-h-screen flex-col ${ownerPreview ? 'memorial-public--owner-preview' : ''}`}
      data-testid="memorial-public-page"
      data-memorial-theme={appearanceProps['data-memorial-theme']}
      data-memorial-accent={appearanceProps['data-memorial-accent']}
      data-memorial-candle={appearanceProps['data-memorial-candle']}
      data-memorial-wallpaper={appearanceProps['data-memorial-wallpaper']}
      style={appearanceProps.style}
    >
      {appearanceProps.wallpaperUrl ? (
        <div
          className="memorial-public__wallpaper-image pointer-events-none fixed inset-0 z-0"
          style={{ backgroundImage: `url(${appearanceProps.wallpaperUrl})` }}
          aria-hidden
        />
      ) : null}
      {ownerPreview && manageUrl ? (
        <MemorialOwnerToolbar
          manageUrl={manageUrl}
          publicUrl={publicMemorialUrl}
          onCopyLink={copyLink}
          onScrollToQr={() => scrollToSection('qr-keepsakes')}
        />
      ) : null}

      <MemorialHero
        person={person}
        memorial={memorial}
        coverUrl={coverUrl}
        portraitUrl={portraitUrl}
        onShare={handleShare}
        onScrollToQr={() => scrollToSection('qr-keepsakes')}
        reactions={reactions}
      />

      {visibleTabs.length > 0 ? (
        <MemorialPublicNav
          tabs={visibleTabs}
          activeId={activeTab}
          onSelect={scrollToSection}
        />
      ) : null}

      <main className="memorial-public__main flex-1">
        {sections.show_life_story ? (
          <section ref={setSectionRef('life-story')} id="life-story" className="memorial-public__section">
            <h2 className="memorial-public__section-title">Life story</h2>
            {hasLifeStoryContent ? (
              <div className="memorial-public__card p-6 sm:p-8">
                {biography ? (
                  <p className="memorial-public__prose whitespace-pre-wrap">{biography}</p>
                ) : null}
                {writtenMemories?.length > 0 ? (
                  <ul className={`space-y-4 ${biography ? 'mt-8' : ''}`}>
                    {writtenMemories.map((m) => (
                      <li key={m.id} className="rounded-xl border border-[hsl(38_14%_86%)] bg-white/90 p-4">
                        <p className="font-medium text-[hsl(24_14%_16%)]">{m.title}</p>
                        {m.source_date ? (
                          <p className="text-[10px] tabular-nums text-[hsl(32_10%_46%)]">{m.source_date}</p>
                        ) : null}
                        {m.excerpt ? (
                          <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{m.excerpt}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {timeline?.length > 0 ? (
                  <div className="mt-8">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(32_10%_42%)]">Timeline</h3>
                    <ol className="memorial-public__timeline relative mt-4 space-y-4">
                      {timeline.map((ev, i) => (
                        <li key={ev.story_id || `${ev.kind}-${ev.date}-${i}`} className="relative">
                          <span className="memorial-public__timeline-dot" aria-hidden />
                          <p className="text-sm font-medium text-[hsl(24_14%_16%)]">{ev.label}</p>
                          <p className="text-xs tabular-nums text-[hsl(32_10%_42%)]">
                            {formatDisplayDate(ev.date)}
                            {ev.place ? ` · ${ev.place}` : ''}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                <p className="mt-6 text-[11px] italic text-[hsl(32_8%_48%)]">Written with care from the family archive.</p>
              </div>
            ) : (
              <div className="memorial-public__empty">
                <p>
                  {ownerPreview
                    ? 'Add a life story from the memorial workspace.'
                    : 'The family has not added a public life story yet.'}
                </p>
                {ownerPreview ? (
                  <OwnerEmptyNote>Biography, featured memories, and timeline appear here when you publish them.</OwnerEmptyNote>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {sections.show_gallery && (hasGalleryContent || ownerPreview) ? (
          <section ref={setSectionRef('gallery')} id="gallery" className="memorial-public__section">
            <h2 className="memorial-public__section-title">Gallery</h2>
            {hasGalleryContent ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                {photos.map((p) => (
                  <figure key={p.id || p.title} className="memorial-public__gallery-item">
                    {p.thumbnail_url || p.image_url ? (
                      <img
                        src={resolveAssetUrl(BACKEND, p.thumbnail_url || p.image_url)}
                        alt=""
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover"
                      />
                    ) : (
                      <div className="aspect-[4/3] w-full bg-gradient-to-br from-[hsl(42_28%_96%)] to-[hsl(38_18%_90%)]" />
                    )}
                    {(p.caption || p.title) ? (
                      <figcaption className="px-3 py-2.5 text-xs leading-relaxed text-[hsl(32_10%_42%)]">
                        {p.caption || p.title}
                      </figcaption>
                    ) : null}
                  </figure>
                ))}
              </div>
            ) : (
              <div className="memorial-public__empty">
                <p>No public gallery photos yet.</p>
                {ownerPreview ? (
                  <OwnerEmptyNote>Select photos in the memorial workspace to share them here.</OwnerEmptyNote>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {(sections.show_voice || sections.show_soundtrack) && (hasVoiceContent || hasMusicContent || ownerPreview) ? (
          <section ref={setSectionRef('voice-music')} id="voice-music" className="memorial-public__section">
            <MemorySoundtrackSection
              music={music}
              soundtrack={soundtrack}
              voiceMemories={voiceMemories}
              showVoice={sections.show_voice}
              showSoundtrack={sections.show_soundtrack}
              ownerPreview={ownerPreview}
            />
          </section>
        ) : null}

        {sections.show_videos && (hasVideoContent || ownerPreview) ? (
          <section ref={setSectionRef('videos')} id="videos" className="memorial-public__section">
            <h2 className="memorial-public__section-title">Videos</h2>
            {hasVideoContent ? (
              <div className="space-y-6">
                {profileVideo ? <MemorialPublicVideoCard item={{ video_link: profileVideo, title: 'Profile video' }} /> : null}
                {externalVideos.map((v, i) => (
                  <MemorialPublicVideoCard key={v.id || v.video_link?.video_url || i} item={v} />
                ))}
                {videoMemories.map((m) => (
                  <MemorialPublicVideoCard key={m.id} item={{ title: m.title, description: m.excerpt, video_link: m.video_link }} />
                ))}
              </div>
            ) : (
              <div className="memorial-public__empty">
                <p>No public videos yet.</p>
                <OwnerEmptyNote>Add video links in the memorial workspace when you are ready.</OwnerEmptyNote>
              </div>
            )}
          </section>
        ) : null}

        {sections.show_tributes ? (
          <section ref={setSectionRef('tributes')} id="tributes" className="memorial-public__section">
            <h2 className="memorial-public__section-title flex items-center gap-2">
              <MessageSquare className="h-3.5 w-3.5" />
              Tributes
            </h2>
            {tributes.length > 0 ? (
              <ul className="mb-8 space-y-4">
                {tributes.map((t) => (
                  <li key={t.id || `${t.author_name}-${t.message?.slice(0, 20)}`} className="memorial-public__tribute-card">
                    <div className="flex items-start gap-2">
                      {t.reaction_type ? (
                        <span className="text-lg" aria-hidden>{tributeReactionEmoji(t.reaction_type)}</span>
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[hsl(24_14%_16%)]">{t.author_name}</p>
                        {t.relationship ? <p className="text-xs text-[hsl(32_10%_42%)]">{t.relationship}</p> : null}
                        {t.created_at ? (
                          <p className="text-[10px] text-[hsl(32_10%_46%)]">
                            {new Date(t.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        ) : null}
                        <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-[hsl(32_10%_38%)]">{t.message}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-6 text-sm text-[hsl(32_10%_42%)]">Be the first to leave a kind word for the family.</p>
            )}
            <LeaveTributeForm
              slug={slug}
              allowTributes={allowTributes}
              requireEmail={requireEmailForTributes}
              onSubmitted={onTributeSubmitted}
            />
          </section>
        ) : null}

        {sections.show_reactions && enableReactions ? (
          <section ref={setSectionRef('remembrance')} id="remembrance" className="memorial-public__section">
            <h2 className="memorial-public__section-title">Quiet remembrance</h2>
            <div className="memorial-public__card p-6 sm:p-8">
              <MemorialReactions
                slug={slug}
                initial={reactions || {}}
                showCounts={showReactionCounts}
                className="text-left sm:text-center"
                candleStyle={appearanceProps['data-memorial-candle']}
              />
            </div>
          </section>
        ) : null}

        {(sections.show_qr || sections.show_keepsakes) ? (
          <section ref={setSectionRef('qr-keepsakes')} id="qr-keepsakes" className="memorial-public__section">
            <h2 className="memorial-public__section-title">QR &amp; keepsakes</h2>
            <div className="memorial-public__card p-6 text-center sm:p-10">
              <QrCode className="mx-auto h-10 w-10 text-[hsl(38_36%_42%)]/70" strokeWidth={1.25} />
              <p className="mt-4 font-serif text-xl text-[hsl(24_14%_18%)]">Scan to return to this memorial</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-[hsl(32_10%_42%)]">
                Share at a service or on a printed keepsake. This dynamic QR opens the published memorial page only — never a private profile.
              </p>
              {sections.show_qr && qrImageSrc ? (
                <img src={qrImageSrc} alt="Memorial QR code" className="mx-auto mt-6 h-40 w-40 rounded-lg border border-[hsl(38_14%_86%)] bg-white p-2 shadow-sm" />
              ) : null}
              {sections.show_qr && dynamicQrUrl ? (
                <p className="mt-4 break-all text-xs text-[hsl(32_10%_46%)]">{dynamicQrUrl}</p>
              ) : publicMemorialUrl ? (
                <p className="mt-4 break-all text-xs text-[hsl(32_10%_46%)]">{publicMemorialUrl}</p>
              ) : null}
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={copyLink}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {dynamicQrUrl ? 'Copy QR link' : 'Copy link'}
                </Button>
                {sections.show_qr && qrImageSrc ? (
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={downloadQr}>
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download QR
                  </Button>
                ) : null}
              </div>
            </div>

            {sections.show_keepsakes && treasuredThings.length > 0 ? (
              <div className="memorial-public__card mt-8 p-6 sm:p-8">
                <h3 className="flex items-center gap-2 font-serif text-lg text-[hsl(24_14%_18%)]">
                  <Gem className="h-4 w-4 text-[#8B7355]/80" strokeWidth={1.25} />
                  Treasured keepsakes
                </h3>
                <ul className="mt-5 grid gap-4 sm:grid-cols-2">
                  {treasuredThings.map((item) => (
                    <li key={item.id} className="overflow-hidden rounded-xl border border-[#c9a961]/25 bg-white shadow-sm">
                      {item.image_url ? (
                        <img src={resolveAssetUrl(BACKEND, item.image_url)} alt="" className="h-36 w-full object-cover" loading="lazy" />
                      ) : (
                        <div className="flex h-28 items-center justify-center bg-[#c9a961]/8">
                          <Gem className="h-8 w-8 text-[#8b7355]/40" strokeWidth={1.25} />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-serif text-base text-[hsl(24_14%_16%)]">{item.title}</h4>
                        {item.category && POSSESSION_CATEGORY_LABELS[item.category] ? (
                          <span className="mt-1 inline-block rounded-full border border-[#c9a961]/30 bg-[#c9a961]/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#8b7355]">
                            {POSSESSION_CATEGORY_LABELS[item.category]}
                          </span>
                        ) : null}
                        {item.why_it_matters ? (
                          <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{item.why_it_matters}</p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {sections.show_keepsakes ? (
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {[
                  { title: MEMORIAL_COPY.keepsakeCard, body: 'A printed card with QR code for memory boxes or service packs.' },
                  { title: MEMORIAL_COPY.keepsakePlaque, body: 'Legacy Link Plaque — a physical QR link to this published memorial.' },
                ].map((item) => (
                  <div key={item.title} className="rounded-xl border border-dashed border-[hsl(38_14%_82%)] bg-white/70 p-4 text-left">
                    <p className="font-medium text-[hsl(24_14%_16%)]">{item.title}</p>
                    <p className="mt-2 text-sm text-[hsl(32_10%_42%)]">{item.body}</p>
                    <p className="mt-3 text-[10px] uppercase tracking-wide text-[hsl(32_8%_48%)]">Physical add-on — planned</p>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {sections.show_support && support?.enabled && support?.url ? (
          <section ref={setSectionRef('support')} id="support" className="memorial-public__section">
            <h2 className="memorial-public__section-title">Support</h2>
            <div className="memorial-public__card p-6 sm:p-8">
              <h3 className="font-serif text-xl text-[hsl(24_14%_18%)]">{support.label || 'Support the family'}</h3>
              {support.note ? (
                <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_42%)]">{support.note}</p>
              ) : null}
              <p className="mt-3 text-xs text-[hsl(32_10%_46%)]">
                This link opens an external support page chosen by the family.
              </p>
              <Button type="button" className="mt-5 rounded-full bg-[hsl(152_28%_22%)] hover:bg-[hsl(152_28%_18%)]" asChild>
                <a href={support.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {support.label || 'Support the family'}
                </a>
              </Button>
            </div>
          </section>
        ) : null}

        {sections.show_atmosphere && (hasAtmosphere || ownerPreview) ? (
          <div ref={setSectionRef('atmosphere')}>
            {hasAtmosphere ? (
              <MemorialAtmosphereSection atmosphere={atmosphere} />
            ) : ownerPreview ? (
              <section id="atmosphere" className="memorial-public__section">
                <h2 className="memorial-public__section-title">Remembered through the senses</h2>
                <div className="memorial-public__empty">
                  <p>Add sensory details in the memorial Appearance workspace.</p>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}

        <p className="memorial-public__footer-note">
          Shared with care by the family. Only content they have chosen appears here.
        </p>

        <MemorialPrivacyFooter />
      </main>

      <PublicFooter variant="compact" />
    </div>
  );
}

export default MemorialPublicExperience;
